import {useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {Backpack, Home, Users} from 'lucide-react';
import {GROUP_TYPES, SelectOption} from '@chekin/core';
import {getReservationTypeOptions} from './labels';
import {Button, RadioCardList} from '@chekinapp/ui';

type SelectableGroupType = GROUP_TYPES.family | GROUP_TYPES.group | GROUP_TYPES.tourist;

type GroupTypeCard = {
  icon: React.ReactNode;
  hintKey: string;
};

const GROUP_TYPE_CARDS: Partial<Record<GROUP_TYPES, GroupTypeCard>> &
  Record<SelectableGroupType, GroupTypeCard> = {
  [GROUP_TYPES.family]: {icon: <Home size={20} />, hintKey: 'group_type_family_hint'},
  [GROUP_TYPES.group]: {icon: <Users size={20} />, hintKey: 'group_type_group_hint'},
  [GROUP_TYPES.tourist]: {
    icon: <Backpack size={20} />,
    hintKey: 'group_type_tourist_hint',
  },
};

type ReservationTypeSectionProps = {
  groupType: GROUP_TYPES;
  onSubmit: () => void;
  disabled?: boolean;
  className?: string;
  submitLabel?: string;
};

function ReservationTypeForm({
  groupType,
  onSubmit,
  disabled,
  className = '',
  submitLabel,
}: ReservationTypeSectionProps) {
  const {t} = useTranslation();
  const {control, handleSubmit} = useFormContext<{
    group_type?: SelectOption<undefined, GROUP_TYPES>;
  }>();
  const reservationTypeOptions = getReservationTypeOptions(t);
  const defaultGroupType = reservationTypeOptions.find(
    option => option.value === groupType,
  );
  const defaultOption =
    groupType !== GROUP_TYPES.single ? defaultGroupType : reservationTypeOptions[0];

  const cardOptions = reservationTypeOptions.map(option => {
    const card = GROUP_TYPE_CARDS[option.value];

    return {
      value: option.value,
      label: option.label,
      description: card && t(card.hintKey),
      icon: card?.icon,
    };
  });

  return (
    <div className={className}>
      <Controller
        name="group_type"
        control={control}
        defaultValue={defaultOption}
        render={({field}) => (
          <RadioCardList
            options={cardOptions}
            value={field.value?.value ?? null}
            onChange={value =>
              field.onChange(
                reservationTypeOptions.find(option => option.value === value),
              )
            }
          />
        )}
      />
      <Button
        disabled={disabled}
        onClick={handleSubmit(onSubmit)}
        variant="primary"
        className="reservation-type-form__button mb-7 mt-7 flex w-full max-w-none sm:mb-0"
      >
        {submitLabel ?? t('save_changes')}
      </Button>
    </div>
  );
}

export {ReservationTypeForm};
